import React, { useState, useEffect } from 'react'
import { Save, Menu } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import Breadcrumb from '../../components/Common/Breadcrumb'
import HelpModal from '../../components/HelpModal/HelpModal'
import submoduleService from '../../services/submoduleService'
import menuItemService from '../../services/menuItemService'
import { toast } from 'react-toastify'

const SubmoduleMasterEdit = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const submoduleData = location.state?.submoduleData || {}
  const id = submoduleData.id || submoduleData._id

  const [formData, setFormData] = useState({
    submoduleCode: '',
    name: '',
    displayName: '',
    description: '',
    menuItem: '',
    type: 'Master',
    sortOrder: 0,
    isActive: true,
    isDisplay: true
  })
  const [menuItemOptions, setMenuItemOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(!!id)
  const [showHelp, setShowHelp] = useState(false)

  useEffect(() => {
    const loadItem = async () => {
      if (!id) {
        setInitialLoading(false)
        return
      }
      try {
        setInitialLoading(true)
        const res = await submoduleService.getSubmoduleById(id)
        if (res?.success && res.data) {
          const d = res.data
          setFormData({
            submoduleCode: d.submoduleCode || '',
            name: d.name || '',
            displayName: d.displayName || '',
            description: d.description || '',
            menuItem: d.menuItem?._id || d.menuItem || '',
            type: d.type || 'Master',
            sortOrder: d.sortOrder ?? 0,
            isActive: d.isActive !== false,
            isDisplay: d.isDisplay !== false
          })
        }
      } catch (err) {
        toast.error('Failed to load module')
        navigate('/settings/submodule-master')
      } finally {
        setInitialLoading(false)
      }
    }
    loadItem()
  }, [id, navigate])

  useEffect(() => {
    const load = async () => {
      try {
        const res = await menuItemService.getAllMenuItems({ includeInactive: true })
        if (res?.success && Array.isArray(res.data)) {
          setMenuItemOptions(res.data.map(item => ({ value: item._id, label: item.label || item.menuCode })))
        }
      } catch (err) {
        console.error(err)
      }
    }
    load()
  }, [])

  const handleInputChange = (e) => {
    const { name, value, type: inputType } = e.target
    if (inputType === 'checkbox') {
      const checked = e.target.checked
      setFormData(prev => ({ ...prev, ...(name === 'disabled' ? { isActive: !checked } : { isDisplay: checked }) }))
      return
    }
    setFormData(prev => ({ ...prev, [name]: name === 'sortOrder' ? (value === '' ? 0 : parseInt(value, 10)) : value }))
  }

  const handleSave = async () => {
    if (!id) return
    if (!formData.submoduleCode?.trim() || !formData.name?.trim()) {
      toast.error('Module Code and Title are required')
      return
    }
    try {
      setLoading(true)
      const payload = {
        submoduleCode: formData.submoduleCode.trim(),
        name: formData.name.trim(),
        displayName: formData.displayName?.trim() || '',
        description: formData.description?.trim() || '',
        menuItem: formData.menuItem || null,
        type: formData.type || 'Master',
        sortOrder: Number(formData.sortOrder) || 0,
        isActive: formData.isActive,
        isDisplay: formData.isDisplay
      }
      const res = await submoduleService.updateSubmodule(id, payload)
      if (res?.success) {
        toast.success('Module updated')
        navigate('/settings/submodule-master')
      } else {
        toast.error(res?.message || 'Failed to update')
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update')
    } finally {
      setLoading(false)
    }
  }

  const breadcrumbItems = [
    { label: 'Settings', path: '/settings', onClick: () => navigate('/settings') },
    { label: 'Masters', path: '/settings', onClick: () => navigate('/settings') },
    { label: 'Module Management', path: '/settings/submodule-master', onClick: () => navigate('/settings/submodule-master') },
    { label: 'Edit Module', path: '', isCurrent: true }
  ]

  const helpContent = (
    <>
      <h3>Edit Module</h3>
      <p>Update the module code, title, display name, business function, type, order, disabled and is display.</p>
    </>
  )

  if (initialLoading) {
    return (
      <div className="sales-dashboard">
        <div className="breadcrumb-section"><Breadcrumb items={breadcrumbItems} showBackButton={false} /></div>
        <div className="text-center py-5">Loading...</div>
      </div>
    )
  }

  if (!id) {
    return (
      <div className="sales-dashboard">
        <div className="breadcrumb-section"><Breadcrumb items={breadcrumbItems} showBackButton={false} /></div>
        <div className="text-center py-5">No module selected. <button type="button" className="btn btn-link" onClick={() => navigate('/settings/submodule-master')}>Back to list</button></div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          showBackButton={false}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <Menu size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Module Management Entry</h1>
          <p>Update module configuration</p>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Module Master Entry</h2>
        </div>

        <form className="form-content" onSubmit={(e) => e.preventDefault()}>
          <div className="section-header">
            <h3>Module Information</h3>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="submoduleCode" className="form-label required">Module Code</label>
              <input type="text" id="submoduleCode" name="submoduleCode" value={formData.submoduleCode} onChange={handleInputChange} className="form-input" placeholder="e.g. SALES_QUOTATION" />
            </div>
            <div className="form-group">
              <label htmlFor="name" className="form-label required">Title</label>
              <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} className="form-input" placeholder="e.g. Sales Quotation" />
            </div>
            <div className="form-group">
              <label htmlFor="displayName" className="form-label">Display Name</label>
              <input type="text" id="displayName" name="displayName" value={formData.displayName} onChange={handleInputChange} className="form-input" placeholder="Label in UI" />
            </div>
            <div className="form-group">
              <label htmlFor="menuItem" className="form-label">Business Function (Module Name)</label>
              <select id="menuItem" name="menuItem" value={formData.menuItem} onChange={handleInputChange} className="form-input">
                <option value="">Select Module Name</option>
                {menuItemOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="type" className="form-label">Type</label>
              <select id="type" name="type" value={formData.type} onChange={handleInputChange} className="form-input">
                <option value="Master">Master</option>
                <option value="Transaction">Transaction</option>
                <option value="Reports">Reports</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="sortOrder" className="form-label">Order</label>
              <input type="number" id="sortOrder" name="sortOrder" min={0} value={formData.sortOrder} onChange={handleInputChange} className="form-input" />
            </div>
            <div className="form-group d-flex align-items-center gap-2">
              <input type="checkbox" id="disabled" name="disabled" checked={!formData.isActive} onChange={handleInputChange} className="form-input" />
              <label htmlFor="disabled" className="form-label mb-0">Disabled</label>
            </div>
            <div className="form-group d-flex align-items-center gap-2">
              <input type="checkbox" id="isDisplay" name="isDisplay" checked={formData.isDisplay} onChange={handleInputChange} className="form-input" />
              <label htmlFor="isDisplay" className="form-label mb-0">Is Display</label>
            </div>
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label htmlFor="description" className="form-label">Description</label>
              <textarea id="description" name="description" rows={2} value={formData.description} onChange={handleInputChange} className="form-input" placeholder="Optional" />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-primary" onClick={handleSave} disabled={loading}>
              <Save size={16} />
              {loading ? 'Saving...' : 'Save'}
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/settings/submodule-master')}>Cancel</button>
          </div>
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Edit Module – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default SubmoduleMasterEdit
